'use client';

import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { VideoSlide, VideoThumb, VideoDialog, getPoster, getVideoKind, getYouTubeId } from '@/_components/products/VideoPlayer';
import { YouTubePayler } from '@/_components/products/YouTubePlayerAdvanced';
import { buildGalleryList } from '@/_components/products/imageGallery';

const isVideoItem = (item) => {
    if (!item) return false;
    const kind = getVideoKind(item);
    return kind === 'youtube' || kind === 'custom';
};

/* ── Inline video player (used inside FullPageGallery to avoid z-index clash with Dialog) ── */
const InlineVideoPlayer = ({ item, defaultPoster }) => {
    const kind    = getVideoKind(item);
    const videoId = kind === 'youtube' ? getYouTubeId(item) : null;

    if (kind === 'youtube' && videoId) {
        return (
            <div style={{ width: '100%', maxWidth: 900, margin: 'auto', aspectRatio: '16/9' }}>
                <iframe
                    src={`https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0`}
                    title={item.title || 'Video'}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    style={{ width: '100%', height: '100%', border: 'none', borderRadius: 10 }}
                />
            </div>
        );
    }

    if (kind === 'custom' && item.itemImageSrc) {
        return (
            <div style={{ width: '100%', maxWidth: 900, margin: 'auto' }}>
                {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
                <video
                    controls
                    autoPlay
                    style={{ width: '100%', maxHeight: '62vh', borderRadius: 10, background: '#000' }}
                >
                    <source src={item.itemImageSrc} />
                </video>
            </div>
        );
    }

    return null;
};

/* ── Full-page Gallery Overlay ─────────────────────────────────── */
const FullPageGallery = ({ images, activeIndex, onClose, onIndexChange, productName, defaultPoster }) => {
    const item = images[activeIndex];

    useEffect(() => {
        const handleKey = (e) => {
            if (e.key === 'Escape') onClose();
            if (e.key === 'ArrowLeft')  onIndexChange((p) => (p - 1 + images.length) % images.length);
            if (e.key === 'ArrowRight') onIndexChange((p) => (p + 1) % images.length);
        };
        document.addEventListener('keydown', handleKey);
        const prev = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.removeEventListener('keydown', handleKey);
            document.body.style.overflow = prev;
        };
    }, [images.length, onClose, onIndexChange]);

    return createPortal(
        <div className="fsg-overlay">

            {/* ── Header ── */}
            <div className="fsg-header">
                <div className="fsg-header-left">
                    <i className="pi pi-images fsg-header-icon" />
                    <div>
                        <span className="fsg-header-label">Photo Gallery</span>
                        <h3 className="fsg-header-title">{productName || 'Product Images'}</h3>
                    </div>
                </div>
                <div className="fsg-header-right">
                    {images.length > 1 && (
                        <span className="fsg-counter-badge">{activeIndex + 1} / {images.length}</span>
                    )}
                    <button className="fsg-close-btn" onClick={onClose} aria-label="Close gallery">
                        <i className="pi pi-times" />
                    </button>
                </div>
            </div>

            {/* ── Main image / video area ── */}
            <div className="fsg-main">
                {images.length > 1 && (
                    <button
                        className="fsg-nav fsg-nav--prev"
                        onClick={() => onIndexChange((p) => (p - 1 + images.length) % images.length)}
                        aria-label="Previous"
                    >
                        <i className="pi pi-chevron-left" />
                    </button>
                )}

                <div className="fsg-img-wrap" key={activeIndex}>
                    {item?.type === 'image' ? (
                        // eslint-disable-next-line @next/next/no-img-element
                        <img src={item.itemImage || item.itemImageSrc} alt={item.alt} className="fsg-img" />
                    ) : isVideoItem(item) ? (
                        <InlineVideoPlayer item={item} defaultPoster={defaultPoster} />
                    ) : null}
                </div>

                {images.length > 1 && (
                    <button
                        className="fsg-nav fsg-nav--next"
                        onClick={() => onIndexChange((p) => (p + 1) % images.length)}
                        aria-label="Next"
                    >
                        <i className="pi pi-chevron-right" />
                    </button>
                )}
            </div>

            {/* ── Thumbnail strip ── */}
            {images.length > 1 && (
                <div className="fsg-thumbs">
                    {images.map((img, i) => (
                        <button
                            key={i}
                            className={`fsg-thumb${activeIndex === i ? ' fsg-thumb--active' : ''}`}
                            onClick={() => onIndexChange(i)}
                            aria-label={`View image ${i + 1}`}
                        >
                            {isVideoItem(img) ? (
                                <div style={{ width: '100%', height: '100%', position: 'relative', overflow: 'hidden' }}>
                                    <VideoThumb item={img} defaultPoster={defaultPoster} />
                                </div>
                            ) : (
                                // eslint-disable-next-line @next/next/no-img-element
                                <img src={img.itemImageSrc} alt={img.alt} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                            )}
                            {activeIndex === i && <span className="fsg-thumb-dot" />}
                        </button>
                    ))}
                </div>
            )}

            <style>{`
                /* ── Overlay root ── */
                .fsg-overlay {
                    position: fixed;
                    inset: 0;
                    z-index: 9999;
                    display: flex;
                    flex-direction: column;
                    background: #ffffff;
                    animation: fsg-slide-in 0.22s ease;
                }
                @keyframes fsg-slide-in {
                    from { opacity: 0; }
                    to   { opacity: 1; }
                }

                /* ── Header ── */
                .fsg-header {
                    background: var(--primary-color, #0b3163);
                    padding: 0.8rem 1.5rem;
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    flex-shrink: 0;
                    border-bottom: 2px solid rgba(27,184,167,0.35);
                    box-shadow: 0 4px 20px rgba(0,0,0,0.4);
                }
                .fsg-header-left {
                    display: flex;
                    align-items: center;
                    gap: 0.85rem;
                    min-width: 0;
                }
                .fsg-header-icon {
                    color: var(--secondary-color, #1bb8a7);
                    font-size: 1.4rem;
                    flex-shrink: 0;
                }
                .fsg-header-label {
                    display: block;
                    color: rgba(27,184,167,0.75);
                    font-size: 0.62rem;
                    font-weight: 700;
                    letter-spacing: 0.12em;
                    text-transform: uppercase;
                    line-height: 1;
                    margin-bottom: 0.25rem;
                }
                .fsg-header-title {
                    color: #ffffff;
                    font-size: 1rem;
                    font-weight: 600;
                    margin: 0;
                    line-height: 1.25;
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    max-width: 55vw;
                }
                .fsg-header-right {
                    display: flex;
                    align-items: center;
                    gap: 0.85rem;
                    flex-shrink: 0;
                }
                .fsg-counter-badge {
                    background: rgba(27,184,167,0.12);
                    border: 1px solid rgba(27,184,167,0.4);
                    color: var(--secondary-color, #1bb8a7);
                    font-size: 0.78rem;
                    font-weight: 700;
                    padding: 0.28rem 0.9rem;
                    border-radius: 999px;
                    letter-spacing: 0.05em;
                    white-space: nowrap;
                }
                .fsg-close-btn {
                    width: 38px;
                    height: 38px;
                    border-radius: 50%;
                    background: rgba(255,255,255,0.06);
                    border: 1.5px solid rgba(255,255,255,0.18);
                    color: #ffffff;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    cursor: pointer;
                    font-size: 0.9rem;
                    transition: background 0.2s, border-color 0.2s, color 0.2s;
                    flex-shrink: 0;
                }
                .fsg-close-btn:hover {
                    background: rgba(27,184,167,0.2);
                    border-color: var(--secondary-color, #1bb8a7);
                    color: var(--secondary-color, #1bb8a7);
                }

                /* ── Main image area ── */
                .fsg-main {
                    flex: 1;
                    display: flex;
                    align-items: center;
                    position: relative;
                    overflow: hidden;
                    padding: 2rem 5.5rem;
                    min-height: 0;
                }
                .fsg-img-wrap {
                    flex: 1;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    height: 100%;
                    min-height: 0;
                    animation: fsg-fade 0.25s ease;
                }
                @keyframes fsg-fade {
                    from { opacity: 0; transform: scale(0.96); }
                    to   { opacity: 1; transform: scale(1); }
                }
                .fsg-img {
                    max-width: 100%;
                    max-height: 100%;
                    object-fit: contain;
                    border-radius: 10px;
                    filter: drop-shadow(0 6px 24px rgba(11,49,99,0.12));
                }

                /* ── Nav arrows ── */
                .fsg-nav {
                    position: absolute;
                    top: 50%;
                    transform: translateY(-50%);
                    z-index: 2;
                    width: 52px;
                    height: 52px;
                    border-radius: 50%;
                    background: #ffffff;
                    border: 2px solid rgba(11,49,99,0.18);
                    color: var(--primary-color, #0b3163);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    cursor: pointer;
                    font-size: 1rem;
                    transition: background 0.2s, border-color 0.2s, color 0.2s, transform 0.2s;
                    box-shadow: 0 2px 12px rgba(11,49,99,0.12);
                }
                .fsg-nav:hover {
                    background: var(--primary-color, #0b3163);
                    border-color: var(--primary-color, #0b3163);
                    color: #ffffff;
                    transform: translateY(-50%) scale(1.1);
                }
                .fsg-nav--prev { left: 1.25rem; }
                .fsg-nav--next { right: 1.25rem; }

                /* ── Thumbnail strip ── */
                .fsg-thumbs {
                    display: flex;
                    gap: 10px;
                    padding: 0.85rem 1.5rem;
                    background: #f8fafc;
                    border-top: 1.5px solid rgba(11,49,99,0.1);
                    overflow-x: auto;
                    flex-shrink: 0;
                    justify-content: center;
                    scrollbar-width: thin;
                    scrollbar-color: #1bb8a7 transparent;
                }
                .fsg-thumb {
                    width: 76px;
                    height: 76px;
                    flex-shrink: 0;
                    border-radius: 10px;
                    border: 2px solid rgba(11,49,99,0.12);
                    background: #ffffff;
                    cursor: pointer;
                    overflow: hidden;
                    position: relative;
                    transition: border-color 0.18s, transform 0.18s, box-shadow 0.18s;
                    padding: 5px;
                }
                .fsg-thumb:hover:not(.fsg-thumb--active) {
                    border-color: rgba(27,184,167,0.55);
                    transform: scale(1.05);
                }
                .fsg-thumb--active {
                    border-color: var(--secondary-color, #1bb8a7) !important;
                    box-shadow: 0 0 0 3px rgba(27,184,167,0.25), 0 4px 12px rgba(11,49,99,0.12);
                    transform: scale(1.1);
                    background: rgba(27,184,167,0.06);
                }
                .fsg-thumb-dot {
                    position: absolute;
                    bottom: 3px;
                    left: 50%;
                    transform: translateX(-50%);
                    width: 6px;
                    height: 6px;
                    border-radius: 50%;
                    background: var(--secondary-color, #1bb8a7);
                }

                /* ── Mobile ── */
                @media (max-width: 767px) {
                    .fsg-header { padding: 0.65rem 1rem; }
                    .fsg-header-title { font-size: 0.85rem; max-width: 48vw; }
                    .fsg-main { padding: 1rem 3.25rem; }
                    .fsg-nav { width: 40px; height: 40px; font-size: 0.82rem; }
                    .fsg-nav--prev { left: 0.5rem; }
                    .fsg-nav--next { right: 0.5rem; }
                    .fsg-thumb { width: 60px; height: 60px; }
                    .fsg-thumbs { padding: 0.65rem 1rem; justify-content: flex-start; }
                }
                @media (max-width: 480px) {
                    .fsg-header-title { max-width: 42vw; font-size: 0.78rem; }
                    .fsg-header-icon { font-size: 1.1rem; }
                    .fsg-thumb { width: 52px; height: 52px; }
                    .fsg-counter-badge { font-size: 0.7rem; padding: 0.2rem 0.6rem; }
                }
            `}</style>
        </div>,
        document.body
    );
};

/* ── Video main-area preview ──────────────────────────────────── */
const VideoMainPreview = ({ item, defaultPoster, onPlay }) => {
    const [hovered, setHovered] = React.useState(false);
    const poster = item.videoCoverFull || getPoster(item, defaultPoster);

    return (
        <div
            onClick={onPlay}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            style={{
                position: 'relative', width: '100%', height: '100%',
                cursor: 'pointer', overflow: 'hidden', borderRadius: 12,
                background: '#ffffff',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
        >
            {/* Poster */}
            {poster && (
                // eslint-disable-next-line @next/next/no-img-element
                <img
                    src={poster}
                    alt={item.alt || 'Video'}
                    style={{
                        position: 'absolute', inset: 0,
                        width: '100%', height: '100%',
                        objectFit: 'contain',
                        transition: 'transform 0.4s ease',
                        transform: hovered ? 'scale(1.03)' : 'scale(1)',
                    }}
                />
            )}

            {/* Hover veil */}
            {hovered && (
                <div style={{
                    position: 'absolute', inset: 0,
                    background: 'rgba(0,0,0,0.12)',
                    transition: 'background 0.2s ease',
                }} />
            )}

            {/* Top-left video badge */}
            <div style={{
                position: 'absolute', top: 10, left: 10, zIndex: 2,
                display: 'flex', alignItems: 'center', gap: 6,
                background: 'var(--primary-color)',
                borderRadius: 8, padding: '5px 10px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.18)',
                transform: hovered ? 'scale(1.05)' : 'scale(1)',
                transition: 'transform 0.2s ease',
            }}>
                <i className="pi pi-play-circle" style={{ color: '#fff', fontSize: '0.78rem' }} />
                <span style={{ color: '#fff', fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.04em' }}>
                    Video
                </span>
            </div>
        </div>
    );
};

/* ── Main Gallery Component ────────────────────────────────────── */
export const Gallery = ({ data = [], coverImageId, coverImage, productName }) => {
    const images = useMemo(() => buildGalleryList(data, coverImage), [data, coverImage]);
    const defaultPoster = useMemo(
        () => images.find((i) => i.type === 'image')?.itemImageSrc ?? null,
        [images]
    );

    const [activeIndex, setActiveIndex] = useState(0);
    const [fsOpen, setFsOpen] = useState(false);
    const [videoOpen, setVideoOpen] = useState(false);

    // Initialise synchronously so the correct flex-direction is used on first paint —
    // avoids the vertical→horizontal strip flash caused by CSS media query lag.
    const [isMobile, setIsMobile] = useState(
        () => typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches
    );
    useEffect(() => {
        const mq = window.matchMedia('(max-width: 767px)');
        const handler = (e) => setIsMobile(e.matches);
        mq.addEventListener('change', handler);
        return () => mq.removeEventListener('change', handler);
    }, []);

    const handleIndexChange = useCallback((val) => setActiveIndex(val), []);

    const renderThumb = (item, index) => {
        const isActive = activeIndex === index;
        const isVideo  = isVideoItem(item);
        return (
            <div
                key={index}
                className={`eg-thumb${isActive ? ' eg-thumb--active' : ''}`}
                onClick={() => setActiveIndex(index)}
                onMouseEnter={(e) => { if (!isActive) e.currentTarget.style.borderColor = 'rgba(27,184,167,0.55)'; }}
                onMouseLeave={(e) => { if (!isActive) e.currentTarget.style.borderColor = 'rgba(11,49,99,0.12)'; }}
            >
                {isVideo ? (
                    <VideoThumb item={item} defaultPoster={defaultPoster} />
                ) : (
                    // eslint-disable-next-line @next/next/no-img-element
                    <img
                        src={item.itemImageSrc}
                        alt={item.alt}
                        style={{ width: '100%', height: '100%', objectFit: 'contain', padding: '6px' }}
                    />
                )}
                {isActive && <span className="eg-thumb__dot" />}
            </div>
        );
    };

    const activeItem = images[activeIndex];

    return (
        <div className="eg-root">

            {/* ── Gallery layout ── */}
            <div className="eg-wrap" style={{ flexDirection: isMobile ? 'column' : 'row' }}>

                {/* Thumbnail strip */}
                <div
                    className="eg-strip"
                    style={{
                        flexDirection: isMobile ? 'row' : 'column',
                        width:         isMobile ? '100%' : '72px',
                        maxHeight:     isMobile ? 'none' : '500px',
                        overflowX:     isMobile ? 'auto' : 'hidden',
                        overflowY:     isMobile ? 'hidden' : 'auto',
                        order:         isMobile ? 1 : -1,
                    }}
                >
                    {images.map((item, i) => renderThumb(item, i))}
                </div>

                {/* Main image */}
                <div
                    className="eg-main"
                    onClick={() => setFsOpen(true)}
                    style={{ cursor: 'zoom-in' }}
                    title="Click to view full gallery"
                >
                    {/* zoom hint badge */}
                    <span className="eg-zoom-hint">
                        <i className="pi pi-search-plus" /> Full View
                    </span>

                    {/* nav arrows — stop propagation so they don't open fullscreen too */}
                    {images.length > 1 && (
                        <>
                            <button
                                className="eg-nav eg-nav--prev"
                                aria-label="Previous image"
                                onClick={(e) => { e.stopPropagation(); setActiveIndex((p) => (p - 1 + images.length) % images.length); }}
                            >
                                <i className="pi pi-chevron-left" aria-hidden="true" />
                            </button>
                            <button
                                className="eg-nav eg-nav--next"
                                aria-label="Next image"
                                onClick={(e) => { e.stopPropagation(); setActiveIndex((p) => (p + 1) % images.length); }}
                            >
                                <i className="pi pi-chevron-right" aria-hidden="true" />
                            </button>
                        </>
                    )}

                    {/* image counter */}
                    {images.length > 1 && (
                        <span className="eg-counter">
                            {activeIndex + 1} / {images.length}
                        </span>
                    )}

                    {/* active media */}
                    {activeItem?.type === 'image' ? (
                        // eslint-disable-next-line @next/next/no-img-element
                        <img
                            key={activeIndex}
                            src={activeItem.itemImage || activeItem.itemImageSrc}
                            alt={activeItem.alt}
                            className="eg-main__img"
                        />
                    ) : isVideoItem(activeItem) ? (
                        <div
                            style={{ position: 'absolute', inset: 0 }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <VideoMainPreview
                                item={activeItem}
                                defaultPoster={defaultPoster}
                                onPlay={() => setVideoOpen(true)}
                            />
                            {videoOpen && (
                                <VideoDialog visible onHide={() => setVideoOpen(false)} title={productName || activeItem.title}>
                                    {getYouTubeId(activeItem)
                                        ? <YouTubePayler videoId={getYouTubeId(activeItem)} />
                                        : /* eslint-disable-next-line jsx-a11y/media-has-caption */
                                          <video controls autoPlay style={{ width: '100%' }}>
                                              <source src={activeItem.itemImageSrc} />
                                          </video>
                                    }
                                </VideoDialog>
                            )}
                        </div>
                    ) : null}
                </div>
            </div>

            {/* ── Full-page overlay ── */}
            {fsOpen && (
                <FullPageGallery
                    images={images}
                    activeIndex={activeIndex}
                    onClose={() => setFsOpen(false)}
                    onIndexChange={handleIndexChange}
                    productName={productName}
                    defaultPoster={defaultPoster}
                />
            )}

            {/* ── Styles ── */}
            <style>{`
                .eg-root { position: relative; width: 100%; }

                .eg-wrap {
                    display: flex;
                    flex-direction: row;
                    gap: 14px;
                    align-items: stretch;
                }

                .eg-strip {
                    display: flex;
                    flex-direction: column;
                    gap: 10px;
                    width: 72px;
                    max-height: 500px;
                    overflow-y: auto;
                    overflow-x: hidden;
                    flex-shrink: 0;
                    padding: 2px;
                    order: -1;
                    scrollbar-width: thin;
                    scrollbar-color: #1bb8a7 transparent;
                }

                .eg-main {
                    flex: 1;
                    border-radius: 14px;
                    border: 1.5px solid rgba(27,184,167,0.25);
                    background: #ffffff;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    min-height: 360px;
                    max-height: 500px;
                    overflow: hidden;
                    position: relative;
                    box-shadow: 0 4px 20px rgba(27,184,167,0.1);
                    transition: border-color 0.2s, box-shadow 0.2s;
                }
                .eg-main:hover {
                    border-color: rgba(27,184,167,0.5);
                    box-shadow: 0 6px 28px rgba(27,184,167,0.18);
                }

                .eg-main__img {
                    max-height: 460px;
                    max-width: 100%;
                    object-fit: contain;
                    padding: 24px 32px;
                    display: block;
                    margin: auto;
                    animation: eg-fadeIn 0.22s ease;
                    pointer-events: none;
                }

                /* zoom hint */
                .eg-zoom-hint {
                    position: absolute;
                    top: 10px;
                    right: 10px;
                    z-index: 2;
                    background: var(--primary-color, #0b3163);
                    color: #ffffff;
                    font-size: 0.68rem;
                    font-weight: 600;
                    padding: 4px 10px;
                    border-radius: 999px;
                    display: flex;
                    align-items: center;
                    gap: 4px;
                    opacity: 0;
                    transition: opacity 0.2s;
                    pointer-events: none;
                    letter-spacing: 0.04em;
                }
                .eg-main:hover .eg-zoom-hint { opacity: 1; }

                .eg-thumb {
                    width: 66px;
                    height: 66px;
                    flex-shrink: 0;
                    cursor: pointer;
                    border-radius: 10px;
                    border: 2px solid rgba(11,49,99,0.12);
                    box-shadow: 0 1px 4px rgba(11,49,99,0.06);
                    overflow: hidden;
                    background: #ffffff;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    transition: border-color 0.18s, transform 0.18s, box-shadow 0.18s;
                    position: relative;
                }
                .eg-thumb--active {
                    border-color: #1bb8a7 !important;
                    box-shadow: 0 0 0 3px rgba(27,184,167,0.28), 0 4px 12px rgba(0,0,0,0.4);
                    background: rgba(27,184,167,0.1);
                    transform: scale(1.06);
                }
                .eg-thumb__dot {
                    position: absolute;
                    bottom: 4px;
                    left: 50%;
                    transform: translateX(-50%);
                    width: 5px;
                    height: 5px;
                    border-radius: 50%;
                    background: #1bb8a7;
                }

                .eg-nav {
                    position: absolute;
                    top: 50%;
                    transform: translateY(-50%);
                    z-index: 2;
                    background: rgba(27,184,167,0.15);
                    border: 1px solid rgba(27,184,167,0.3);
                    color: #1bb8a7;
                    width: 32px;
                    height: 32px;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    cursor: pointer;
                    transition: background 0.18s;
                    font-size: 0.75rem;
                }
                .eg-nav:hover { background: rgba(27,184,167,0.35); }
                .eg-nav--prev { left: 10px; }
                .eg-nav--next { right: 10px; }

                .eg-counter {
                    position: absolute;
                    bottom: 12px;
                    right: 14px;
                    z-index: 2;
                    background: rgba(27,184,167,0.12);
                    border: 1px solid rgba(27,184,167,0.3);
                    color: #0f766e;
                    font-size: 11px;
                    font-weight: 700;
                    padding: 2px 8px;
                    border-radius: 20px;
                    letter-spacing: 0.5px;
                    pointer-events: none;
                }

                @keyframes eg-fadeIn {
                    from { opacity: 0; transform: scale(0.97); }
                    to   { opacity: 1; transform: scale(1); }
                }

                @media (max-width: 767px) {
                    .eg-wrap { flex-direction: column; gap: 10px; }
                    .eg-strip {
                        order: 1;
                        flex-direction: row;
                        width: 100%;
                        max-height: none;
                        overflow-x: auto;
                        overflow-y: hidden;
                        padding: 4px 2px;
                        gap: 8px;
                    }
                    .eg-thumb { width: 58px; height: 58px; border-radius: 8px; }
                    .eg-main { min-height: 260px; max-height: 340px; border-radius: 12px; }
                    .eg-main__img { max-height: 300px; padding: 16px 20px; }
                    .eg-nav { width: 28px; height: 28px; }
                    .eg-nav--prev { left: 6px; }
                    .eg-nav--next { right: 6px; }
                }

                @media (max-width: 480px) {
                    .eg-thumb { width: 52px; height: 52px; }
                    .eg-main { min-height: 220px; max-height: 280px; }
                    .eg-main__img { max-height: 250px; padding: 12px 16px; }
                }
            `}</style>
        </div>
    );
};

export default Gallery;
